import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'

// A utility to get a full time zone name such as 'Pacific Daylight Time' or
// an abbreviation such as 'PDT' given a time zone (e.g. 'America/Los_Angeles'),
// date, locale, and time zone name format
export const getTimeZoneName = ({
  date = new Date(),
  locale = 'en-US',
  timeZone,
  timeZoneName = 'long',
}: {
  date?: Date
  locale?: string
  timeZone: string
  timeZoneName?: Intl.DateTimeFormatOptions['timeZoneName']
}) => {
  // The full time zone name or abbreviation is generated by formatting a date into an array representation of
  // the date string that contains the full time zone name or abbreviation and extracting the name or abbreviation
  // from the array.
  // E.g. `parts` would look like this: [{type: 'day', value: '12'}, {type: 'literal', value: ', '}, {type: 'timeZoneName', value: 'Pacific Daylight Time'}]
  // Then the full time zone name or abbreviation can be found from the value of the object with type 'timeZoneName'.
  const parts = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    timeZone,
    timeZoneName,
  }).formatToParts(date)
  const longTimeZoneName = parts?.find(({ type }) => type === 'timeZoneName')?.value

  return longTimeZoneName
}

/**
 * Split ISO string into date and time values without converting timezone
 *
 * This function takes an ISO8601 datetime string (without timezone info) and a timezone,
 * and splits it into separate date and time components while treating the input as if
 * it's already in the specified timezone.
 *
 * No timezone conversion or translation should be happening for this method.
 *
 * @param iso8601DateString - ISO8601 datetime string without timezone (e.g., "2025-08-07T18:00:00")
 * @param timezone - Target timezone (e.g., "UTC", "America/New_York")
 * @returns Object with date and time components
 *
 * @example
 * ```typescript
 * const result = splitISO8601String({
 *   iso8601DateString: "2025-08-07T18:00:00",
 *   timezone: "UTC"
 * })
 * // Returns: { date: "2025-08-07", time: "18:00:00" }
 * ```
 */
export const splitISO8601String = ({
  iso8601DateString,
  timezone,
}: {
  iso8601DateString: string
  timezone: string
}) => {
  // `zonedTimeToUtc` takes a separate datetime string (with no timezone) and a timezone and marries them together
  // into a Date object that represents a fully formed datetime.
  const utcDate = zonedTimeToUtc(iso8601DateString, timezone)
  const builtDate = formatInTimeZone(utcDate, timezone, 'yyyy-MM-dd')
  const builtTime = formatInTimeZone(utcDate, timezone, 'HH:mm:ss')
  return {
    date: builtDate,
    time: builtTime,
  }
}
